import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import RollText from '@/components/generals/roll-text'
import SectionLabel from '@/components/generals/section-label'
import TransitionLink from '@/components/motion/transition-link'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import { process } from '@/constants/content'

// Growth Foundations as a horizontal run: the page scroll moves the four stages
// sideways while a measuring tape along the bottom fills with progress.
const Process: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const progressRef = useRef<HTMLDivElement>(null)

    useGSAP(() => {
        const stage = stageRef.current
        const track = trackRef.current

        if (!stage || !track) return

        const q = gsap.utils.selector(sectionRef)
        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.desktop, () => {
            const distance = (): number => Math.max(0, track.scrollWidth - window.innerWidth)

            const run = gsap.to(track, {
                x: () => -distance(),
                ease: 'none',
                scrollTrigger: {
                    trigger: stage,
                    start: 'top top',
                    end: () => `+=${distance()}`,
                    pin: true,
                    scrub: 0.8,
                    invalidateOnRefresh: true,
                    onUpdate: (self) => {
                        if (progressRef.current) gsap.set(progressRef.current, { scaleX: self.progress })
                    }
                }
            })

            q('[data-process-image]').forEach(image => {
                gsap.fromTo(image, { xPercent: -7 }, {
                    xPercent: 7,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: image.parentElement,
                        containerAnimation: run,
                        start: 'left right',
                        end: 'right left',
                        scrub: true
                    }
                })
            })

            q('[data-process-number]').forEach(number => {
                gsap.fromTo(number, { xPercent: 40, '--wdth': 75 }, {
                    xPercent: -10,
                    '--wdth': 125,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: number,
                        containerAnimation: run,
                        start: 'left right',
                        end: 'right left',
                        scrub: true
                    }
                })
            })
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section id='process' ref={sectionRef} data-scene='ink' aria-labelledby='process-title' className='relative'>
            <div ref={stageRef} className='relative motion-safe:lg:h-svh motion-safe:lg:overflow-hidden'>
                <div ref={trackRef} className='flex flex-col motion-safe:lg:h-full motion-safe:lg:w-max motion-safe:lg:flex-row'>
                    <div className='flex flex-col justify-center px-5 py-28 md:px-8 motion-safe:lg:w-[44vw] motion-safe:lg:py-0 motion-safe:lg:pr-16'>
                        <SectionLabel index='05'>
                            Growth foundations
                        </SectionLabel>

                        <h2 id='process-title' className='type-h1 mt-8'>
                            From first <em className='type-serif text-pop'>workshop</em> to launch.
                        </h2>

                        <p className='type-lead mt-8 max-w-[36ch] text-quiet'>
                            We shape your message, design your website and build it in Webflow. Four stages, one connected team, nothing lost in between.
                        </p>
                    </div>

                    {process.map((stage, index) => (
                        <article
                            key={stage.index}
                            className={cn(
                                'relative flex flex-col gap-10 border-t border-rule px-5 py-16 md:px-8',
                                'motion-safe:lg:h-full motion-safe:lg:w-[min(74vw,1180px)] motion-safe:lg:flex-row motion-safe:lg:gap-14 motion-safe:lg:border-l motion-safe:lg:border-t-0 motion-safe:lg:px-14 motion-safe:lg:py-0',
                                index % 2 === 0 ? 'motion-safe:lg:items-center' : 'motion-safe:lg:flex-row-reverse motion-safe:lg:items-end motion-safe:lg:pb-24'
                            )}
                        >
                            <div className='shrink-0 motion-safe:lg:w-[38%]'>
                                <p data-process-number='' aria-hidden='true' className='type-display text-outline leading-[0.8] text-pop'>
                                    {stage.index}
                                </p>

                                <p className='type-label mt-8 text-pop'>
                                    {stage.stage}
                                </p>

                                <h3 className='type-h2 mt-3'>
                                    {stage.title}
                                </h3>

                                <p className='mt-5 max-w-[34ch] text-lg text-quiet'>
                                    {stage.text}
                                </p>
                            </div>

                            <div
                                className={cn(
                                    'relative aspect-[4/3] w-full overflow-hidden bg-fore/5 motion-safe:lg:aspect-auto',
                                    index % 2 === 0 ? 'motion-safe:lg:h-[60vh]' : 'motion-safe:lg:h-[48vh]'
                                )}
                            >
                                <img
                                    data-process-image=''
                                    src={stage.image}
                                    alt={stage.imageAlt}
                                    loading='lazy'
                                    decoding='async'
                                    className='absolute inset-y-0 -left-[8%] h-full w-[116%] max-w-none object-cover'
                                />
                            </div>
                        </article>
                    ))}

                    <div className='flex flex-col justify-center gap-8 border-t border-rule px-5 py-20 md:px-8 motion-safe:lg:w-[42vw] motion-safe:lg:border-l motion-safe:lg:border-t-0 motion-safe:lg:px-14 motion-safe:lg:py-0'>
                        <p className='type-h1'>
                            Then we keep it <em className='type-serif text-pop'>growing.</em>
                        </p>

                        <p className='max-w-[36ch] text-lg text-quiet'>
                            Training at handover, support after launch, and a partnership when you want us to keep improving it.
                        </p>

                        <TransitionLink href='/services' className='group inline-flex w-fit items-center gap-3 bg-volt px-5 py-4 text-sm font-medium text-ink'>
                            <RollText>
                                See every service
                            </RollText>

                            <ArrowUpRight aria-hidden='true' className='size-4' />
                        </TransitionLink>
                    </div>
                </div>

                <div aria-hidden='true' className='pointer-events-none absolute inset-x-8 bottom-6 hidden h-10 motion-safe:lg:block'>
                    <div className='type-label absolute inset-x-0 top-0 flex justify-between text-quiet'>
                        <span>
                            0
                        </span>

                        {process.map(stage => (
                            <span key={stage.index}>
                                {stage.stage}
                            </span>
                        ))}

                        <span>
                            Launch
                        </span>
                    </div>

                    <div className='absolute inset-x-0 bottom-0 h-2 [background-image:repeating-linear-gradient(to_right,var(--env-line)_0_1px,transparent_1px_12px)]' />
                    <div className='absolute inset-x-0 bottom-0 h-4 opacity-50 [background-image:repeating-linear-gradient(to_right,var(--env-fg)_0_1px,transparent_1px_120px)]' />
                    <div ref={progressRef} className='absolute bottom-0 left-0 h-[3px] w-full origin-left scale-x-0 bg-mark' />
                </div>
            </div>
        </section>
    )
}

export default Process
